import { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent,
  DialogTitle, Grid, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  InputAdornment, LinearProgress, Skeleton, Fade, DialogContentText
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Visibility as DetailIcon, Search as SearchIcon, Refresh as RefreshIcon,
  Description as DescriptionIcon, Print as PrintIcon,
  LocalShipping as DeliveryIcon, AssignmentReturn as DeliveryInIcon
} from '@mui/icons-material';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import DeliveryOutModal from './DeliveryOutModal';
import DeliveryInModal from './DeliveryInModal';
import Pagination from '../../common/Pagination';

function DeliveryIndex() {
  const [data, setData] = useState([]);
  const [filteredData1, setFilteredData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, branchId, logout, token } = useAuth();
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);


  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);

  const [deliveryInModalOpen, setDeliveryInModalOpen] = useState(false);
  const [selectedDeliveryForIn, setSelectedDeliveryForIn] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return;
    setLoading(true);
    fetchTableData().finally(() => {
      setLoading(false);
    });
  }, [companyId, token]);

  useEffect(() => {
    applyFilters();
  }, [data, searchTerm]);

  useEffect(() => {
    if (companyId && token && !loading) {
      fetchTableData();
    }
  }, [fromDate, toDate]);

  const fetchTableData = async () => {
    try {
      const response = await fetchData('DeliveryOutApi', `/DeliveryOutList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Delivery Details:', error);
      setData([]);
    }
  };

  const applyFilters = () => {
    if (!Array.isArray(data)) {
      setFilteredData([]);
      return;
    }
    let filtered = [...data];
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.CustomerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ContactNumber1?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.Location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredData(filtered);
  };

  const handleDelete = async (id) => {
    try {
      const response = await deleteData('DeliveryCallApi', id, token);
      if (response && response.responseCode === '1111') {
        await fetchTableData();
      }
    } catch (error) {
      console.error('Error deleting Delivery:', error);
    }
    setConfirmDialogOpen(false);
    setDeleteItemId(null);
  };

  const fetchSingleDelivery = async (deliveryId) => {
    try {
      const response = await fetchData('DeliveryCallApi', `/${deliveryId}`, token);
      let deliveryData = response.response_data || response;
      if (deliveryData.DeliveryCall && deliveryData.DeliveryCallDetails) {
        return {
          ...deliveryData.DeliveryCall,
          DeliveryCallDetails: deliveryData.DeliveryCallDetails
        };
      }
      return deliveryData;
    } catch (error) {
      console.error('Error fetching single Delivery:', error);
      return null;
    }
  };

  const handleModalSave = async () => {
    await fetchTableData();
    setModalOpen(false);
    setSelectedItem(null);
    setModalMode('create');
  };

  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setDeleteItemId(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    setModalMode('create');
  };

  const handleSaveDeliveryIn = async () => {
    await fetchTableData();
    setDeliveryInModalOpen(false);
    setSelectedDeliveryForIn(null);
  };



  const clearFilters = () => {
    setSearchTerm('');
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    setFromDate(firstDay.toISOString().split('T')[0]);
    setToDate(now.toISOString().split('T')[0]);
  };

  const handleRefresh = () => {
    fetchTableData();
  };

  if (!mounted) return null;

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.CustomerName?.toLowerCase().includes(searchLower) ||
      item.ContactNumber1?.toLowerCase().includes(searchLower) ||
      item.Location?.toLowerCase().includes(searchLower) ||
      item.DeliveryNo?.toLowerCase().includes(searchLower) ||
      item.DeliveryInVehicleNo?.toLowerCase().includes(searchLower)
    );
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const columns = [
    { field: 'DeliveryNo', header: 'Delivery No' },
    { field: 'DeliveryDate', header: 'Delivery Date' },
    { field: 'CustomerName', header: 'Customer' },
    { field: 'ContactNumber', header: 'Contact' },
    { field: 'AddressLine', header: 'Address' },
    { field: 'AdvanceAmount', header: 'Advance Amount' },
  ];


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };


  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .table-row {
            /* No hover animations */
          }
        `}</style>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Romans',
        }}>
          {/* Header Section */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: 'Times New Romans',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Delivery Out</span>
                </h4>
              </div>
            </div>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                setSelectedItem(null);
                setModalMode('create');
                setModalOpen(true);
              }}
              sx={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.5,
                '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)' }
              }}
            >
              Create Out
            </Button>
          </div>

          {/* Date Filters, Search and Create Button Row - Horizontally Scrollable */}
          <div style={{
            marginBottom: '12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end',
              minWidth: 'fit-content',
              paddingBottom: '4px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>

              {/* Search - Icon on mobile, always visible on desktop */}
              {isMobile ? (
                searchExpanded ? (
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    autoFocus
                    onBlur={() => {
                      if (!searchTerm) {
                        setSearchExpanded(false);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: '250px',
                      width: '350px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        fontSize: '12px',
                        height: '32px',
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />
                ) : (
                  <Tooltip title="Search" arrow>
                    <Button
                      onClick={() => setSearchExpanded(true)}
                      style={{
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                    </Button>
                  </Tooltip>
                )
              ) : (
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
              )}
            </div>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="delivery in table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    height: '28px'
                  }}>
                    {columns.map((column) => (
                      <TableCell key={column.field} style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        padding: '4px 8px',
                        textAlign: 'left',
                        height: '28px'
                      }}>
                        {column.header}
                      </TableCell>
                    ))}
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      height: '28px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((row) => (
                    <TableRow
                      key={row.Id}
                      sx={{
                        '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                        '&:hover': {
                          backgroundColor: '#f1f5f9',
                          transform: 'scale(1.001)',
                          transition: 'all 0.2s ease-in-out'
                        },
                        borderBottom: '1px solid #f1f5f9'
                      }}
                    >
                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.75rem' }}>
                          {row.DeliveryNo || `DEL-${String(row.Id).padStart(4, '0')}`}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="caption" sx={{ color: 'black', fontSize: '0.75rem' }}>
                          {formatDate(row.DeliveryDate)}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.3, fontSize: '0.75rem' }}>
                          {row.CustomerName || 'N/A'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="caption" sx={{ color: 'black', fontSize: '0.75rem' }}>
                          {row.ContactNumber || 'N/A'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="caption" sx={{ color: 'black', fontSize: '0.75rem' }}>
                          {row.AddressLine || 'N/A'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'black', fontSize: '0.75rem' }}>
                          {row.AdvanceAmount != null ? row.AdvanceAmount : 'N/A'}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ py: 1, textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'flex-end' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={async () => {
                                const fullData = await fetchSingleDelivery(row.Id);
                                setSelectedItem(fullData);
                                setModalMode('view');
                                setModalOpen(true);
                              }}
                              sx={{
                                color: '#3b82f6',
                                backgroundColor: '#eff6ff',
                                width: 28,
                                height: 28,
                                '&:hover': {
                                  backgroundColor: '#dbeafe',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <DetailIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Edit Delivery">
                            <IconButton
                              size="small"
                              onClick={async () => {
                                const fullData = await fetchSingleDelivery(row.Id);
                                setSelectedItem(fullData);
                                setModalMode('edit');
                                setModalOpen(true);
                              }}
                              sx={{
                                color: '#f59e0b',
                                backgroundColor: '#fffbeb',
                                width: 28,
                                height: 28,
                                '&:hover': {
                                  backgroundColor: '#fef3c7',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <EditIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>

                          {row.IsDeliveryInCompleted === false && (
                            <Tooltip title="Delivery In">
                              <IconButton
                                size="small"
                                onClick={async () => {
                                  setSelectedDeliveryForIn(row);
                                  setDeliveryInModalOpen(true);
                                }}
                                sx={{
                                  color: '#10b981',
                                  backgroundColor: '#d1fae5',
                                  width: 28,
                                  height: 28,
                                  '&:hover': {
                                    backgroundColor: '#a7f3d0',
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <DeliveryInIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* <Tooltip title="Print">
                            <IconButton
                              size="small"
                              sx={{
                                color: '#8b5cf6',
                                backgroundColor: '#f5f3ff',
                                width: 28,
                                height: 28,
                                '&:hover': {
                                  backgroundColor: '#ede9fe',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <PrintIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip> */}
                          <Tooltip title="Delete Delivery">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setDeleteItemId(row.Id);
                                setConfirmDialogOpen(true);
                              }}
                              sx={{
                                color: '#ef4444',
                                backgroundColor: '#fef2f2',
                                width: 28,
                                height: 28,
                                '&:hover': {
                                  backgroundColor: '#fecaca',
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          <DeliveryOutModal
            open={modalOpen}
            onClose={() => {
              setModalOpen(false);
              setSelectedItem(null);
              setModalMode('create');
            }}
            onSave={handleModalSave}
            deliveryData={selectedItem}
            mode={modalMode}
          />

          <DeliveryInModal
            open={deliveryInModalOpen}
            onClose={() => {
              setDeliveryInModalOpen(false);
              setSelectedDeliveryForIn(null);
            }}
            onSave={handleModalSave}
            deliveryData={selectedDeliveryForIn}
          />

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{
              fontSize: '13px',
              color: '#666'
            }}>
              Are you sure you want to delete this delivery in record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span>
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

      </div>
    </Fade>
  );
}


export default DeliveryIndex;
